// One-click export of everything (Section 11: Brett must never be locked in).
// Reads every table the signed-in user can see, then downloads it as one Excel
// workbook (a sheet per table), one JSON file, or a CSV per table.

import { fetchAll } from './fetchAll';
import { supabase } from './supabase';

export const EXPORT_TABLES = [
  'contacts', 'contact_emails', 'contact_phones', 'contact_org_history',
  'organizations', 'org_aliases', 'practice_profiles', 'tenant_requirements',
  'buildings', 'suites', 'org_locations', 'building_ownership',
  'deals', 'deal_contacts', 'deal_stage_history', 'deal_lease_terms', 'deal_rent_abatements',
  'fee_components', 'payout_structures', 'payments',
  'interactions', 'interaction_attachments', 'reminders',
  'documents', 'document_extractions', 'extraction_corrections', 'clause_patterns',
  'deal_types', 'deal_stages', 'fee_templates', 'app_settings', 'email_ignore_rules',
  'suggested_contacts', 'review_queue', 'import_batches', 'import_mappings', 'digests',
] as const;
export type ExportTable = (typeof EXPORT_TABLES)[number];

export async function loadTable(table: ExportTable): Promise<Record<string, unknown>[]> {
  // The generated full-text "search" columns are internal; leave them out.
  const rows = await fetchAll((from, to) => supabase.from(table).select('*').order('created_at').range(from, to));
  return (rows as Record<string, unknown>[]).map(({ search: _search, ...rest }) => rest);
}

export async function loadEverything(onProgress?: (table: string) => void): Promise<Record<string, Record<string, unknown>[]>> {
  const out: Record<string, Record<string, unknown>[]> = {};
  for (const t of EXPORT_TABLES) {
    onProgress?.(t);
    out[t] = await loadTable(t);
  }
  return out;
}

function stamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function download(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

/** Cells can't hold arrays/objects: write them as JSON text. */
function flatten(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v !== null && typeof v === 'object' ? JSON.stringify(v) : v])));
}

export async function downloadJson(data: Record<string, unknown[]>) {
  download(`crm-export-${stamp()}.json`, new Blob([JSON.stringify({ exported_at: new Date().toISOString(), tables: data }, null, 2)], { type: 'application/json' }));
}

export async function downloadWorkbook(data: Record<string, Record<string, unknown>[]>) {
  const XLSX = await import('xlsx'); // loaded only when exporting
  const wb = XLSX.utils.book_new();
  for (const [table, rows] of Object.entries(data)) {
    // Excel sheet names max 31 chars.
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flatten(rows)), table.slice(0, 31));
  }
  XLSX.writeFile(wb, `crm-export-${stamp()}.xlsx`);
}

export async function downloadCsv(table: ExportTable) {
  const XLSX = await import('xlsx');
  const rows = await loadTable(table);
  const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(flatten(rows)));
  download(`${table}-${stamp()}.csv`, new Blob([csv], { type: 'text/csv' }));
}
